import { useState, useEffect } from "react";
import { useSearchParams, useParams } from "react-router-dom";

import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import * as services from "./../../services/customer";

const UploadPage = () => {
  const [searchParams] = useSearchParams();
  const { id, type } = useParams();

  const [files, setFiles] = useState([]);

  const loadFiles = async () => {
    const res = await services.getFarmerFiles(id, type);
    setFiles(res.data.data || []);
  };

  useEffect(() => {
    loadFiles();
  }, [id, type]);

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files);

    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);

      const uploadRes = await services.uploadFile(formData, type);

      const fileUrl = uploadRes.data.file;

      await services.saveFarmerFile({
        farmerId: Number(id),
        fileUrl: fileUrl,
        fileName: file.name,
        type: type,
      });

      await loadFiles();
    }
  };

  const handleOpenFile = (file) => {
    if (!file?.fileUrl) return;

    const url = file.fileUrl;
    const ext = file.fileName?.split(".").pop()?.toLowerCase();

    // 🖼 รูป → เปิด tab ใหม่
    if (type === "image") {
      window.open(url, "_blank");
      return;
    }

    // 📄 PDF → preview ใน browser
    if (ext === "pdf") {
      window.open(url, "_blank"); // browser จะ render pdf ให้เลย
      return;
    }

    // 📁 อื่นๆ → เปิด (บางอันจะ download)
    window.open(url, "_blank");
  };

  const handleDelete = async (file, index) => {
    const confirmDelete = window.confirm(`ลบไฟล์ "${file.fileName}" ?`);
    if (!confirmDelete) return;

    try {
      await services.deleteFarmerFile(file.id);

      await loadFiles();
    } catch (err) {
      console.error(err);
      alert("ลบไม่สำเร็จ");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {type === "image" ? "รูปภาพ" : "เอกสาร"}
        </Typography>

        <Button
          variant="contained"
          component="label"
          startIcon={<UploadFileIcon />}
        >
          นำเข้า
          <input
            hidden
            type="file"
            multiple
            accept={type === "image" ? "image/*" : "*"}
            onChange={handleUpload}
          />
        </Button>
      </Box>

      {/* Empty */}
      {files.length === 0 ? (
        <Box
          sx={{
            height: 250,
            border: "2px dashed #ddd",
            borderRadius: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color="text.secondary">
            ยังไม่มี{type === "image" ? "รูปภาพ" : "เอกสาร"}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {files.map((file, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                onClick={() => handleOpenFile(file)}
                sx={{
                  position: "relative",
                  borderRadius: 3,
                  boxShadow: 2,
                  aspectRatio: "1 / 1",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  "&:hover": {
                    boxShadow: 6,
                    transform: "translateY(-4px)",
                    transition: "0.2s",
                  },
                }}
              >
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation(); // 🔥 กันไม่ให้ไป trigger open file
                    handleDelete(file, index);
                  }}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    bgcolor: "rgba(0,0,0,0.5)",
                    color: "#fff",
                    "&:hover": {
                      bgcolor: "rgba(255,0,0,0.8)", // 🔥 hover แล้วแดง
                    },
                    zIndex: 10,
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#f5f5f5",
                  }}
                >
                  {type === "image" ? (
                    <Box
                      component="img"
                      src={file.fileUrl}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover", // 🔥 เต็มช่องแบบไม่เสียทรง
                      }}
                    />
                  ) : (
                    <InsertDriveFileIcon sx={{ fontSize: 60 }} />
                  )}
                </Box>

                {/* Footer */}
                <CardContent
                  sx={{
                    py: 1,
                    px: 1.5,
                    borderTop: "1px solid #eee",
                  }}
                >
                  <Typography variant="body2" noWrap>
                    {file.fileName}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default UploadPage;
